import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Bell, Download, X, Smartphone, Share } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '../App';

const ClientPWAInstall = ({ 
  slug, 
  salonName, 
  brandColor = '#6366f1',
  language = 'it' 
}) => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showBanner, setShowBanner] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);

  // Detect iOS
  useEffect(() => {
    const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(isIOSDevice);
  }, []);

  // Check if app is installed
  useEffect(() => {
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches ||
                         window.navigator.standalone === true;
    setIsInstalled(isStandalone);
  }, []);

  // Listen for install prompt (Android/Chrome)
  useEffect(() => {
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      
      // Show banner if not dismissed
      const dismissed = localStorage.getItem(`pwa-${slug}-dismissed`);
      if (!dismissed) {
        setTimeout(() => setShowBanner(true), 2000);
      }
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    
    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, [slug]);

  // Show banner for iOS users
  useEffect(() => {
    if (isIOS && !isInstalled) {
      const dismissed = localStorage.getItem(`pwa-${slug}-dismissed`);
      if (!dismissed) {
        setTimeout(() => setShowBanner(true), 2000);
      }
    }
  }, [isIOS, isInstalled, slug]);

  // Register service worker for push notifications
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      // Check subscription status
      navigator.serviceWorker.ready.then((registration) => {
        registration.pushManager.getSubscription().then((subscription) => {
          setIsSubscribed(!!subscription);
        });
      });
    }
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      toast.success(language === 'it' ? 'App installata!' : 'App installed!');
      setIsInstalled(true);
      // Enable notifications after install
      setTimeout(() => handleEnableNotifications(), 1000);
    }
    
    setDeferredPrompt(null);
    setShowBanner(false);
  };

  const handleEnableNotifications = async () => {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      toast.error(language === 'it' 
        ? 'Il tuo browser non supporta le notifiche push' 
        : 'Your browser does not support push notifications');
      return;
    }

    setLoading(true);
    try {
      // Register service worker for this salon
      const registration = await navigator.serviceWorker.register(
        `${API}/public/salon/${slug}/sw.js`,
        { scope: `/book/${slug}` }
      );
      
      // Get VAPID key
      const vapidResponse = await axios.get(`${API}/push/vapid-public-key`);
      const vapidPublicKey = vapidResponse.data.public_key;
      
      // Request notification permission
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        toast.error(language === 'it' 
          ? 'Permesso notifiche negato' 
          : 'Notification permission denied');
        setLoading(false);
        return;
      }

      // Subscribe to push
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
      });

      // Save subscription to backend
      await axios.post(`${API}/public/salon/${slug}/push/subscribe`, {
        subscription: subscription.toJSON()
      });

      setIsSubscribed(true);
      toast.success(language === 'it' 
        ? '🔔 Notifiche attivate! Riceverai promemoria per i tuoi appuntamenti.' 
        : '🔔 Notifications enabled! You will receive appointment reminders.');
      
      setShowBanner(false);
    } catch (error) {
      console.error('Push subscription error:', error);
      toast.error(language === 'it' 
        ? 'Errore nell\'attivazione delle notifiche' 
        : 'Error enabling notifications');
    } finally {
      setLoading(false);
    }
  };

  const handleDismiss = () => {
    setShowBanner(false);
    localStorage.setItem(`pwa-${slug}-dismissed`, 'true');
  };

  const handleShowIOSInstructions = () => {
    setShowIOSInstructions(true);
  };

  // iOS share instructions modal
  if (showIOSInstructions) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-4">
        <div className="bg-white dark:bg-slate-800 rounded-2xl w-full max-w-md p-6 animate-slide-up">
          <div className="flex justify-between items-start mb-4">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
              {language === 'it' ? 'Come installare su iPhone' : 'How to install on iPhone'}
            </h3>
            <button 
              onClick={() => setShowIOSInstructions(false)}
              className="p-1 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-full"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <div 
                className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0"
                style={{ backgroundColor: brandColor }}
              >
                1
              </div>
              <div>
                <p className="font-medium text-slate-900 dark:text-white">
                  {language === 'it' ? 'Tocca l\'icona Condividi' : 'Tap the Share icon'}
                </p>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  <Share className="w-4 h-4 inline mr-1" />
                  {language === 'it' ? 'in basso al centro dello schermo' : 'at the bottom center of the screen'}
                </p>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <div 
                className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0"
                style={{ backgroundColor: brandColor }}
              >
                2
              </div>
              <div>
                <p className="font-medium text-slate-900 dark:text-white">
                  {language === 'it' ? 'Scorri e tocca' : 'Scroll and tap'}
                </p>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  "{language === 'it' ? 'Aggiungi alla schermata Home' : 'Add to Home Screen'}"
                </p>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <div 
                className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0"
                style={{ backgroundColor: brandColor }}
              >
                3
              </div>
              <div>
                <p className="font-medium text-slate-900 dark:text-white">
                  {language === 'it' ? 'Tocca "Aggiungi"' : 'Tap "Add"'}
                </p>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  {language === 'it' ? 'in alto a destra' : 'in the top right corner'}
                </p>
              </div>
            </div>
          </div>
          
          <Button 
            onClick={() => setShowIOSInstructions(false)}
            className="w-full mt-6 rounded-full"
            style={{ backgroundColor: brandColor }}
          >
            {language === 'it' ? 'Ho capito!' : 'Got it!'}
          </Button>
        </div>
      </div>
    );
  }

  // Don't show anything if installed and subscribed
  if (isInstalled && isSubscribed) {
    return null;
  }

  // Main banner
  if (showBanner) {
    return (
      <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-96 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl shadow-2xl p-5 z-50 animate-slide-up">
        <button 
          onClick={handleDismiss}
          className="absolute top-3 right-3 p-1.5 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-full transition-colors"
        >
          <X className="w-4 h-4 text-slate-400" />
        </button>
        
        <div className="flex items-start gap-4">
          <div 
            className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg"
            style={{ background: `linear-gradient(135deg, ${brandColor}, ${brandColor}dd)` }}
          >
            <Smartphone className="w-7 h-7 text-white" />
          </div>
          
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-slate-900 dark:text-white text-lg">
              {language === 'it' ? `Installa ${salonName}` : `Install ${salonName}`}
            </h3>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
              {language === 'it' 
                ? 'Aggiungi alla home e ricevi promemoria per i tuoi appuntamenti' 
                : 'Add to home screen and get appointment reminders'}
            </p>
            
            <div className="flex flex-wrap gap-2 mt-4">
              {isIOS ? (
                <Button 
                  onClick={handleShowIOSInstructions}
                  className="rounded-full text-white shadow-md"
                  style={{ backgroundColor: brandColor }}
                  data-testid="install-pwa-ios-btn"
                >
                  <Share className="w-4 h-4 mr-2" />
                  {language === 'it' ? 'Come installare' : 'How to install'}
                </Button>
              ) : deferredPrompt ? (
                <Button 
                  onClick={handleInstall}
                  className="rounded-full text-white shadow-md"
                  style={{ backgroundColor: brandColor }}
                  data-testid="install-pwa-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  {language === 'it' ? 'Installa App' : 'Install App'}
                </Button>
              ) : null}
              
              {!isSubscribed && (
                <Button 
                  onClick={handleEnableNotifications}
                  variant="outline"
                  className="rounded-full"
                  disabled={loading}
                  data-testid="enable-notifications-btn"
                >
                  <Bell className="w-4 h-4 mr-2" />
                  {loading 
                    ? (language === 'it' ? 'Attivazione...' : 'Enabling...') 
                    : (language === 'it' ? 'Attiva notifiche' : 'Enable notifications')}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

// Helper function to convert VAPID key
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export default ClientPWAInstall;
